import React, { useEffect, useRef, useState } from 'react';
import WrapperContainer from '../../Wrappers/WrapperContainer';
import { yupResolver } from '@hookform/resolvers';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { usePreventWindowUnload, useQuery } from '@panel/commons/hooks';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { StepperOperations, StepperSelectors } from '../../../ducks/stepper';
import { priceStepSelectors, priceStepOperations } from '../../../ducks/price';
import { PriceStepSchema } from './validations';
import { getStepMainSelectors } from '../../../ducks/propertyType';
import PriceSelector from './PriceSelector';
import { getDevice, site } from '@panel/commons/helpers';
import { Divider, PriceSelectorContainer, ExpensesContainer, CreditAcceptanceContainer } from './styles';
import FormSwitch from '../../commons/FormSwitch';
import { bodyManagerPriceStep } from '../../../utils/bodyManagerPriceStep';
import { SavePostingOperations } from '../../../ducks/savePosting';
import { getIsAvailableCreditAcceptance, operationTypesConfig, portalsWithCreditAcceptance } from './priceConfig';
import { updateFormValues } from '../../../ducks/features/actions';
import { FeaturesSelectors } from '../../../ducks/features';
import { getStepperState } from '../../../ducks/stepper/selectors';
import useFormIsDirty from '../../../hooks/useFormIsDirty';
import { transformValue } from '../../../utils/numberFormatHelper';
import { currencyOptionsBySiteClean } from '@panel/commons/helpers/currenciesBySite';

const PriceStep = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { setFormIsDirty } = useFormIsDirty();
  const formRef = useRef(null);
  const featuresState = useSelector(FeaturesSelectors.getFeaturesState);
  const stepperState = useSelector(getStepperState);
  const mainData = useSelector(getStepMainSelectors);
  const { priceInfo, priceCompleted, priceLoading, priceFeatures, realEstateType } = useSelector(
    priceStepSelectors.getPriceStepState
  );
  const { snackAction } = useSelector(StepperSelectors.getStepperState);
  const currencySelectOptions = currencyOptionsBySiteClean(site.id);
  const [postingId, isEdition] = useQuery(useLocation(), ['postingId', 'isEdition']);
  const [operationTypes, setOperationTypes] = useState(null);
  const [withoutExpenses, setWithoutExpenses] = useState(false);
  const [creditAcceptance, setCreditAcceptance] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [initialPriceInfoValue, setInitialPriceInfoValue] = useState(true);
  const { register, handleSubmit, errors, setValue, formState, getValues, trigger, watch } = useForm({
    mode: 'onSubmit',
    shouldFocusError: true,
    context: { requiredFields: mainData?.formValues.operationType || [] },
    resolver: yupResolver(PriceStepSchema),
    reValidateMode: 'onChange'
  });
  const expensesval = watch('expenses');

  useEffect(() => {
    if (expensesval !== undefined && expensesval !== null) {
      dispatch(updateFormValues({ CFT6: transformValue(expensesval) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expensesval]);

  useEffect(() => {
    if (portalsWithCreditAcceptance.includes(site.id)) {
      setValue('creditAcceptance', creditAcceptance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditAcceptance]);

  useEffect(() => {
    if (portalsWithCreditAcceptance.includes(site.id)) {
      register('creditAcceptance');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isMobile = getDevice() === 'mobile';

  usePreventWindowUnload(formState.isDirty);

  useEffect(() => {
    setFormIsDirty(formState.isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.isDirty]);

  const getIsValidForm = async () => {
    const res = await trigger();
    return res;
  };

  useEffect(() => {
    const keys = Object.keys(errors);
    const hasEmptyError = keys.some(key => errors[key]?.type === 'required');
    const hasPriceUSDError = keys.some(key => errors[key]?.type === 'is-less-than-main-price');
    if (hasEmptyError) {
      dispatch(
        StepperOperations.setSnackAction({
          action: 'somePriceError',
          result: 'ERROR',
          component: t('professionalPubli.price.snackError'),
          section: 'Publicador profesionales - Paso Precio'
        })
      );
    }
    if (hasPriceUSDError) {
      dispatch(
        StepperOperations.setSnackAction({
          action: 'is-less-than-main-price',
          result: 'ERROR',
          component: t('professionalPubli.price.op.validation.error'),
          section: 'Publicador profesionales - Paso Precio'
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    // Saneamiento del state que dispara el Snackbar:
    return () => {
      dispatch(StepperOperations.setSnackAction({ action: '', result: '' }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snackAction.result]);

  // Reviso si al menos uno de los precios tiene un valor válido
  const hasValidAmount = array =>
    array?.some(obj => obj.prices?.some(price => price.amount !== null && price.amount !== '0' && price.amount !== 0));

  useEffect(() => {
    // setteo los datos guardados en la BD en RHF
    const hasDataOnDB =
      isEdition ||
      hasValidAmount(priceInfo) ||
      (stepperState.lastStep >= stepperState.currentStep && !stepperState.loadingStepperState);
    if (priceCompleted && !priceLoading && priceInfo && firstLoad && operationTypes) {
      if (hasDataOnDB) {
        priceInfo.forEach(item => {
          const key = operationTypesConfig[item.operation_type];
          if (key) {
            setValue(key, transformValue(item.prices[0]?.amount));
          }
          if (site.isPeru) {
            setValue(`${key}-second`, item.prices[1] ? transformValue(item.prices[1]?.amount) : '');
          }
        });
        // intento cargar el valor para el switch de apto credito
        if (portalsWithCreditAcceptance.includes(site.id) && priceFeatures) {
          const creditFeature = priceFeatures.find(f => f.feature_id === '1000001');
          if (creditFeature) {
            setCreditAcceptance(creditFeature.value === 'true');
          }
        }
        // inicializo el campo de expenses para todos los portales
        if (priceFeatures.length > 0) {
          const expensesFeature = priceFeatures.find(f => f.feature_id === 'CFT6');
          if (expensesFeature) {
            // parseo a int por que viene un float de la bd
            const expensesInt = Math.floor(expensesFeature.value);
            setValue('expenses', transformValue(expensesInt));
            setWithoutExpenses(expensesInt === 0 || expensesInt === '0');
          } else {
            setValue('expenses', '');
          }
          const iptuFeature = priceFeatures.find(f => f.feature_id === 'CFT400');
          if (iptuFeature && site.isBrasil) {
            // parseo a int por que viene un float de la bd
            const iptuInt = Math.floor(iptuFeature.value);
            setValue('IPTU', transformValue(iptuInt));
          }
        }
        setFirstLoad(false);
      } else if (initialPriceInfoValue) {
        priceInfo.forEach(item => {
          const key = operationTypesConfig[item.operation_type];
          if (key) {
            setValue(key, '');
          }
          if (site.isPeru) {
            setValue(`${key}-second`, '');
          }
        });
        if (site.isBrasil) {
          setValue('IPTU', '');
        }
        setInitialPriceInfoValue(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    priceCompleted,
    priceLoading,
    priceInfo,
    setValue,
    stepperState,
    firstLoad,
    operationTypes,
    isEdition,
    initialPriceInfoValue
  ]);

  useEffect(() => {
    if (mainData?.formValues.operationType.length > 0 && !featuresState?.fetchError) {
      const initialState = mainData?.formValues.operationType.map(type => {
        return { operation_type: type, prices: [{ amount: null, currency: 'MXN' }] };
      });
      dispatch(priceStepOperations.getFormValues({ postingId, initialState }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, postingId, mainData?.formValues.operationType]);

  useEffect(() => {
    // si el dato ya esta en redux por lo seteo
    if (
      mainData?.formValues?.operationType.length > 0 &&
      postingId &&
      !mainData?.formValuesError &&
      !mainData?.formValuesLoading
    ) {
      setOperationTypes(mainData?.formValues?.operationType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingId, mainData?.formValues.operationType]);

  const onSubmit = data => {
    const exp = getValues('expenses')?.replace(/[,.]/g, '');
    const payload = {
      body: {
        ...bodyManagerPriceStep(
          { ...data, expenses: exp?.length > 0 ? Number(exp) : exp },
          priceInfo,
          portalsWithCreditAcceptance.includes(site.id),
          site.isBrasil,
          site.isPeru
        ),
        postingId
      },
      step: 'STEP_PRICE'
    };
    dispatch(SavePostingOperations.savePostingDB(payload));
  };

  const handleSwitchChange = () => {
    setWithoutExpenses(!withoutExpenses);
  };

  const handleCreditSwitchChange = () => {
    setCreditAcceptance(!creditAcceptance);
  };

  useEffect(() => {
    if (withoutExpenses) {
      setValue('expenses', 0);
    } else {
      const expensesFeature = priceFeatures?.find(f => f.feature_id === 'CFT6');
      const expensesFeatureValue = expensesFeature?.length > 0 ? expensesFeature.value : '';
      setValue('expenses', expensesFeatureValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withoutExpenses]);

  const onCurrencySelectChange = (currency, operationId) => {
    const newPriceInfo = priceInfo?.map(op => {
      if (op.operation_type === operationId) {
        return { ...op, prices: [{ ...op.prices[0], currency }] };
      }
      return op;
    });
    dispatch(priceStepOperations.setPriceInfo(newPriceInfo));
  };

  const getCurrentCurrencyByOpType = operationId => {
    const currentOperation = priceInfo?.find(op => op.operation_type === operationId);
    return currentOperation?.prices[0]?.currency;
  };

  return (
    <WrapperContainer
      id='price-step'
      title={t('professionalPubli.price.title')}
      note={t('professionalPubli.price.mandatory')}
      onSubmit={handleSubmit(onSubmit)}
      btnSubmitText={t('professionalPubli.cta.continue')}
      getIsValidForm={getIsValidForm}
      formValuesLoading={priceLoading || stepperState.loadingStepperState || mainData.formValuesLoading}
      loadError={mainData.formValuesError || featuresState.fetchError || stepperState.getLastStepError}
      resetInfo={() => dispatch(priceStepOperations.resetInfo())}
    >
      <form data-publi='priceStep-form' id='priceStep' ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        {operationTypes?.map(type => {
          return (
            <PriceSelectorContainer key={type}>
              <PriceSelector
                dataPubli={`professionalPubli-priceStep-priceSelector-${type}`}
                label={`${t(`professionalPubli.price.op.${operationTypesConfig[type]}`)}*`}
                placeholder={t(`professionalPubli.price.op.${operationTypesConfig[type]}.placeholder`)}
                register={register}
                hasOptions={currencySelectOptions?.length > 1}
                typesOptions={currencySelectOptions}
                name={operationTypesConfig[type]}
                width={isMobile ? '100%' : '274px'}
                error={t(errors[operationTypesConfig[type]]?.message)}
                onSelect={value => {
                  onCurrencySelectChange(value, type);
                }}
                currentValue={getCurrentCurrencyByOpType(type)}
                priceValue={watch(operationTypesConfig[type])}
                onInputChange={value => {
                  setValue(operationTypesConfig[type], transformValue(value));
                }}
              />
              {site.isPeru && (
                <PriceSelector
                  dataPubli={`professionalPubli-priceStep-priceSelector-USD-${type}`}
                  isSecondPrice
                  label={t(`professionalPubli.price.op.${operationTypesConfig[type]}`)}
                  placeholder={t(`professionalPubli.price.op.${operationTypesConfig[type]}.placeholder`)}
                  register={register}
                  hasOptions={currencySelectOptions?.length > 1}
                  typesOptions={[{ value: 'USD', name: 'USD' }]}
                  name={`${operationTypesConfig[type]}-second`}
                  width={isMobile ? '100%' : '274px'}
                  error={t(errors[`${operationTypesConfig[type]}-second`]?.message)}
                  onSelect={value => {
                    onCurrencySelectChange(value, type);
                  }}
                  currentValue={'USD'}
                  priceValue={watch(`${operationTypesConfig[type]}-second`)}
                  onInputChange={value => {
                    setValue(`${operationTypesConfig[type]}-second`, transformValue(value));
                  }}
                />
              )}
              {getIsAvailableCreditAcceptance(realEstateType, type) && (
                <CreditAcceptanceContainer>
                  <p data-publi='credit-switch-label'>{t('professionalPubli.price.credit.switch')}</p>
                  <FormSwitch
                    id={'switchCreditAcceptance'}
                    dataPanel={'credit-switch'}
                    value={creditAcceptance}
                    handleSwitchChange={handleCreditSwitchChange}
                  />
                </CreditAcceptanceContainer>
              )}
              <Divider />
            </PriceSelectorContainer>
          );
        })}
        {site.isBrasil && (
          <PriceSelectorContainer>
            <PriceSelector
              dataPubli={`professionalPubli-priceStep-priceSelector-IPTU`}
              label={t(`professionalPubli.price.IPTU`)}
              placeholder={t(`professionalPubli.price.IPTU.placeholder`)}
              register={register}
              hasOptions={false}
              name={'IPTU'}
              width={isMobile ? '100%' : '274px'}
              error={t(errors['IPTU']?.message)}
              currentValue={currencySelectOptions[0]?.value}
              priceValue={watch('IPTU')}
              onInputChange={value => {
                setValue('IPTU', transformValue(value));
              }}
            />
            <Divider />
          </PriceSelectorContainer>
        )}
        <PriceSelector
          dataPubli={`professionalPubli-priceStep-priceSelector-expenses`}
          label={t(`professionalPubli.price.expenses`)}
          placeholder={t(`professionalPubli.price.expenses.placeholder`)}
          register={register}
          hasOptions={false}
          name={'expenses'}
          width={isMobile ? '100%' : '274px'}
          error={t(errors['expenses']?.message)}
          currentValue={currencySelectOptions[0]?.value}
          disabled={withoutExpenses}
          priceValue={getValues('expenses')}
          onInputChange={value => {
            setValue('expenses', transformValue(value));
          }}
        />
        <ExpensesContainer>
          <p data-publi='expenses-switch-label'>{t('professionalPubli.price.expenses.switch')}</p>
          <FormSwitch
            id={'switchWithExpenses'}
            dataPanel={'expenses-switch'}
            value={withoutExpenses}
            handleSwitchChange={handleSwitchChange}
          />
        </ExpensesContainer>
      </form>
    </WrapperContainer>
  );
};

export default PriceStep;
